import { useRef, useState } from "react";
import { SessionData } from "./types";
import { uploadDocuments } from "./api";

interface Props {
  data: SessionData;
  update: (patch: Partial<SessionData>) => void;
  onNext: () => void;
  onSkip: () => void;
}

export function DocumentUploadStep({ data, update, onNext, onSkip }: Props) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function handlePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    // de-dupe by filename
    const existing = new Set(data.selectedDocuments.map((f) => f.name));
    const merged = [
      ...data.selectedDocuments,
      ...picked.filter((f) => !existing.has(f.name)),
    ];
    update({ selectedDocuments: merged });
    // reset so picking the same file again still fires onChange
    if (inputRef.current) inputRef.current.value = "";
  }

  function remove(name: string) {
    update({ selectedDocuments: data.selectedDocuments.filter((f) => f.name !== name) });
  }

  async function uploadAndContinue() {
    if (data.sessionId == null || data.sessionToken == null) return;
    setUploading(true);
    setError(null);
    try {
      await uploadDocuments({
        sessionId: data.sessionId,
        token: data.sessionToken,
        files: data.selectedDocuments,
      });
      onNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  const docs = data.selectedDocuments;

  return (
    <div className="wizard-step">
      <header className="wizard-step-header">
        <h2>Upload Your Documents</h2>
        <p>Add all PDF files you need notarized. You can select multiple documents.</p>
      </header>

      {docs.length === 0 ? (
        <div className="wizard-doc-empty">
          <div className="wizard-doc-empty-icon" aria-hidden="true">📄</div>
          <p>No documents added yet</p>
          <p className="wizard-doc-empty-hint">
            Tap "Add PDF Document" below to get started.
          </p>
        </div>
      ) : (
        <div className="wizard-doc-list">
          <div className="wizard-label">
            {docs.length} document{docs.length === 1 ? "" : "s"} selected
          </div>
          {docs.map((file) => (
            <div className="wizard-doc-item" key={file.name}>
              <span className="wizard-doc-icon" aria-hidden="true">📄</span>
              <span className="wizard-doc-name">{file.name}</span>
              <button
                type="button"
                className="wizard-doc-remove"
                aria-label={`Remove ${file.name}`}
                onClick={() => remove(file.name)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="wizard-button-secondary"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        + {docs.length === 0 ? "Add PDF Document" : "Add More Documents"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        multiple
        hidden
        onChange={handlePicked}
      />

      {error && <div className="wizard-error">{error}</div>}

      {docs.length > 0 && (
        <button
          type="button"
          className="wizard-button-primary"
          onClick={uploadAndContinue}
          disabled={uploading}
        >
          {uploading ? "Uploading…" : "Continue"}
        </button>
      )}

      <button
        type="button"
        className="wizard-button-link"
        onClick={() => {
          update({ documentsSkipped: true });
          onSkip();
        }}
        disabled={uploading}
      >
        Skip for now
      </button>
    </div>
  );
}
